import { useTranslation } from 'react-i18next'
import { styled } from '@mui/material/styles'
import Stack from '@mui/material/Stack'
import Divider from '@mui/material/Divider'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import Typography from '@mui/material/Typography'
import Modal from '@mui/material/Modal'
import Button from '@mui/material/Button'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'

import { useAddCoAuthorMutation } from '../../../services/options'
import { getOption, useFetchDepartments } from '../../../utils'
import { CoAuthors } from '../../../types'

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  borderRadius: { xs: 0, sm: 6 },
  boxShadow: 24,
  padding: { xs: 1, sm: 3 },
  width: { xs: '100%', sm: '90%' },
  maxWidth: '70rem',
  height: { xs: '100%', sm: '80%' },
  overflow: 'auto',
}

const Form = styled('form')(() => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  justifyContent: 'space-between',
  flex: '1 0 auto',
}))

interface AddCoAuthorModalProps {
  isOpen: boolean
  close: () => void
  defaultValue?: Partial<CoAuthors>
  onSuccess?: (coAuthor: CoAuthors) => void
}

const CoAuthorSchema = z.object({
  first_name: z.string().min(1),
  last_name: z.string().min(1),
  email: z.string().email(),
  department: z.number().nullable(),
  department_other: z.string().optional().default(''),
}) satisfies z.ZodType<Omit<CoAuthors, 'id'>, z.ZodTypeDef, unknown>
export type CoAuthorSchema = z.infer<typeof CoAuthorSchema>

export default function AddCoAuthorModal({
  isOpen,
  close,
  defaultValue = {},
  onSuccess,
}: AddCoAuthorModalProps) {
  const { t } = useTranslation('abstracts')

  const [addCoAuthor] = useAddCoAuthorMutation()
  const { fetch, loading, departments } = useFetchDepartments()

  const { control, watch, handleSubmit, getValues } = useForm({
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      department: null,
      department_other: '',
      ...defaultValue,
    } satisfies CoAuthorSchema,
    resolver: zodResolver(CoAuthorSchema),
  })

  const onSubmitForm = (
    event: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.preventDefault()
    const values = getValues()

    try {
      const data = CoAuthorSchema.safeParse(values)

      if ('error' in data && !data.success) {
        toast.error(t("Le schéma de donnée n'est pas valide", { ns: 'common' }))
        data.error.issues.forEach(issue => {
          toast.warn(`${issue.path.toString()}: ${issue.message}`, {
            autoClose: 60000,
          })
        })
        console.error(data.error)
      }
      if ('data' in data && data.success) {
        addCoAuthor(data.data)
          .then(response => {
            if ('error' in response) {
              toast.error(t("Erreur lors de l'ajout du co-auteur"))
              console.error(response.error)
            } else {
              toast.success(t('Co-auteur ajouté avec succès'))
              onSuccess?.(response.data)
              close()
            }
          })
          .catch(e => {
            console.error(e)
            toast.error(t("Erreur lors de l'ajout du co-auteur"))
          })
      }
    } catch (e) {
      console.error(e)
      toast.error(t("Le schéma de donnée n'est pas valide", { ns: 'common' }))
      toast.warn(t('Erreur innatendue', { ns: 'common' }))
    }
  }

  const department = watch('department')
  const department_other = watch('department_other')

  return (
    <Modal open={isOpen} onClose={close}>
      <Stack sx={modalStyle} gap="0.5rem">
        <Form onSubmit={onSubmitForm}>
          <Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography margin={0} variant="h2">
                {t("Ajout d'un nouveau co-author")}
              </Typography>
            </Stack>
            <Divider />
          </Stack>

          <Stack gap="1rem" flex="1">
            <Controller
              control={control}
              name="last_name"
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label={t('Nom')}
                  fullWidth
                  error={fieldState.error?.message !== undefined}
                  helperText={fieldState.error?.message}
                  required
                />
              )}
            />
            <Controller
              control={control}
              name="first_name"
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label={t('Prénom')}
                  fullWidth
                  error={fieldState.error?.message !== undefined}
                  helperText={fieldState.error?.message}
                  required
                />
              )}
            />
            <Controller
              control={control}
              name="email"
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label={t('Email')}
                  type="email"
                  fullWidth
                  error={fieldState.error?.message !== undefined}
                  helperText={fieldState.error?.message}
                  required
                />
              )}
            />
            <Controller
              control={control}
              name="department"
              rules={{ required: !department_other }}
              render={({ field, fieldState }) => (
                <Autocomplete
                  id="department-autocomplete"
                  options={departments}
                  onChange={(_, value) => field.onChange(value?.value ?? null)}
                  onBlur={field.onBlur}
                  value={field.value ? getOption(departments, null, field.value) : null}
                  fullWidth
                  loading={loading}
                  onInputChange={(_, newInputValue) => {
                    fetch(newInputValue)
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      name={field.name}
                      disabled={field.disabled}
                      required={!department_other}
                      label={t('Service')}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              )}
            />
            <Controller
              control={control}
              name="department_other"
              rules={{ required: department === null }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label={t('Autre service')}
                  fullWidth
                  required={department === null}
                  error={fieldState.error?.message !== undefined}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Stack>

          <Stack direction="row" justifyContent={'space-between'} paddingBottom="1.5rem">
            <Button variant="outlined" onClick={close}>
              {t('Annuler')}
            </Button>
            <Button
              variant="contained"
              onClick={e => {
                void handleSubmit(
                  () => {
                    onSubmitForm(e)
                  },
                  () => {
                    toast.error(t('Veuillez corriger les erreurs'))
                  }
                )()
              }}
            >
              {t('Ajouter')}
            </Button>
          </Stack>
        </Form>
      </Stack>
    </Modal>
  )
}
